import { useEffect } from "react"
import { captureException } from "@sentry/react"
import {
    initAnalytics,
    trackEvent as trackEventGeneric,
} from "@mallardbay/lib-shared-helpers"
import { globalHistory } from "@reach/router"

import { useUser } from "~contexts/user-provider"
import type { CurrentUserFieldsFragment } from "~graphql/users.generated"
import { useGetUserTrackingDataQuery } from "~graphql/user-tracking-data.generated"

const CUSTOM_PAGE_VIEW = "custom_page_view"

let userId: string | null = null

export default function useAnalytics() {
    const { user } = useUser()
    const { loading: isLoading, data } = useGetUserTrackingDataQuery({
        onError: captureException,
    })

    const userTrackingProps = data?.userTrackingData.props || []

    useEffect(() => {
        const isReady = user?.id && !isLoading
        if (!isReady) return

        setUserId(user.id)
        initAnalytics({
            userId: user.id,
            userTrackingProps,
            onError: (err) => handleError(err),
        })
        initCanny(user)
    }, [user, isLoading, userTrackingProps])

    // Custom page view tracking
    useEffect(() => {
        const unsubscribe = globalHistory.listen(({ location }) => {
            trackEvent(CUSTOM_PAGE_VIEW, {
                pathname: location.pathname,
                search: location.search,
                host: location.host,
            })
        })

        return () => unsubscribe()
    }, [])
}

// Exported for tests
// We do this so we can use it outside of hooks easily
function setUserId(userIdLocal?: string) {
    userId = userIdLocal || null
}

// Use this over the core `trackEvent` from `lib-shared-helpers`
export function trackEvent(
    eventName: string,
    eventParams: Record<string, unknown>
) {
    const paramsToUse = {
        ...eventParams,
        userId,
    }
    trackEventGeneric({
        eventName,
        eventParams: paramsToUse,
        onError: handleError,
    })
}

// Exported for tests
export function handleError(error: Error) {
    if (!process.env.IS_LOCAL) {
        captureException(error)
    } else {
        // eslint-disable-next-line no-console
        console.error(error)
    }
}

// Currently we only use this in dashboard. If we ever need it in web, we should move it to shared-helpers
export function initCanny(user: CurrentUserFieldsFragment) {
    if (!window.Canny) {
        // eslint-disable-next-line no-console
        console.error("Canny is not defined, cannot initialize")
        return
    }

    if (!process.env.GATSBY_CANNY_APP_ID) {
        // eslint-disable-next-line no-console
        console.error("GATSBY_CANNY_APP_ID is not defined, cannot initialize")
        return
    }

    window.Canny("identify", {
        appID: process.env.GATSBY_CANNY_APP_ID,
        user: {
            // Replace these values with the current user's data
            email: user.email,
            name: `${user.first_name} ${user.last_name}`,
            id: user.id,

            // These fields are optional, but recommended:
            avatarURL: user.avatar_url || undefined,
            created: new Date(user.created_at).toISOString(),
        },
    })
}
